/**
 * Fashion Catalog Theme - Alpine.js Entry Point
 *
 * SIMPLIFIED ARCHITECTURE:
 * - Stores: Only UI state (sidebar visibility, loading states)
 * - Components: UI components (sliders, modals, notifications)
 * - server: Handles ALL data operations server-side
 *
 * No duplicate functionality with server framework
 */

// ========================================
// IMPORT ALPINE.JS AND PLUGINS
// ========================================
import Alpine from "alpinejs";
import collapse from "@alpinejs/collapse";

// Register plugins
Alpine.plugin(collapse);

// Make Alpine available globally
window.Alpine = Alpine;
window.tx = function (key, fallback) {
  try {
    var ds = document.documentElement && document.documentElement.dataset;
    var v = ds ? ds[key] : null;
    return (v && v.length) ? v : (fallback || "");
  } catch (e) {
    return fallback || "";
  }
};

// ========================================
// IMPORT STORES
// ========================================
import loaderStore from "./stores/loader.js";
import cartStore from "./stores/cart.js";
import wishlistStore from "./stores/wishlist.js";
import comparisonStore from "./stores/comparison.js";
import themeStore from "./stores/theme.js";
import quickViewStore from "./stores/quickView.js";
import headerStore from "./stores/header.js";
import lightboxStore from "./components/lightbox.js";

// ========================================
// IMPORT COMPONENTS
// ========================================
import notificationsComponent from "./components/notifications.js";
import productSliderComponent from "./components/productSlider.js";
import thumbnailSliderComponent from "./components/thumbnailSlider.js";
import cookieBannerComponent from "./components/cookieBanner.js";
import categoryPageComponent from "./components/categoryPage.js";
import productQuestionsComponent from "./components/productQuestions.js";
import fashionModalComponent from "./components/fashionModal.js";
import productComponent from "./components/product.js";
import returnFormComponent from "./components/returnForm.js";
import orderHistoryManagerComponent from "./components/orderHistoryManager.js";
import scrollToTopComponent from "./components/scrollToTop.js";
import {
  countdownTimer,
  stockUrgency,
  freeShippingProgress,
  countdown
} from "./components/salesUrgency.js";

// ========================================
// ALPINE.JS INITIALIZATION
// ========================================
document.addEventListener("alpine:init", () => {
  const Alpine = window.Alpine;

  // ========================================
  // REGISTER STORES
  // ========================================
  Alpine.store("loader", loaderStore);
Alpine.store("cart", cartStore);
Alpine.store("wishlist", wishlistStore);
Alpine.store("comparison", comparisonStore);
Alpine.store("theme", themeStore);
Alpine.store("quickView", quickViewStore);
Alpine.store("header", headerStore);

  // Lightbox store
  Alpine.store("lightbox", lightboxStore);

  // Analytics store - expose window.Analytics to Alpine components
  if (window.Analytics) {
    Alpine.store("analytics", window.Analytics);
  }

  // ========================================
  // REGISTER COMPONENTS
  // ========================================

  /**
   * Notifications Component - Toast messages
   * Usage: <div x-data="notifications">...</div>
   */
  Alpine.data("notifications", notificationsComponent);

  /**
   * Product Slider Component - Product carousels
   */
  Alpine.data("productSlider", productSliderComponent);

  /**
   * Thumbnail Slider Component - Vertical thumbnail navigation
   * Height synced with main product image
   */
  Alpine.data("thumbnailSlider", thumbnailSliderComponent);

  /**
   * Cookie Banner Component - Neumorphic cookie consent
   */
  Alpine.data("cookieBanner", cookieBannerComponent);

  /**
   * Category Page Component - AJAX filtering, sorting, pagination
   * Also used on manufacturer page with same filtering logic
   */
  Alpine.data("categoryPage", categoryPageComponent);

  /**
   * Product Questions Component - Toggle form, UI interactions
   */
  Alpine.data("productQuestions", productQuestionsComponent);

  /**
   * Fashion Modal Component - Newsletter popup with delay
   */
  Alpine.data("fashionModal", fashionModalComponent);

  /**
   * Print Button Component - Replaces onclick="window.print()"
   */
  Alpine.data("printButton", () => ({
    print() {
      window.print();
    },
  }));

  /**
   * Navigation Button Component - Replaces onclick="history.back()"
   */
  Alpine.data("navigationButton", () => ({
    goBack() {
      history.back();
    },
  }));

  /**
   * Scroll To Target Component - Replaces onclick="document.getElementById().scrollIntoView()"
   */
  Alpine.data("scrollButton", (targetId) => ({
    scrollToTarget() {
      const target = document.getElementById(targetId);
      if (target) {
        target.scrollIntoView({ behavior: "smooth" });
      }
    },
  }));

  /**
   * Mobile Filter Component - Global mobile filter toggle
   */
  Alpine.data("mobileFilter", () => ({
    isOpen: false,

    toggle() {
      this.isOpen = !this.isOpen;
      const overlay = document.getElementById("mobileFilterOverlay");
      const panel = document.getElementById("mobileFilterPanel");

      if (this.isOpen) {
        overlay.classList.remove("hidden");
        setTimeout(() => {
          panel.classList.remove("-translate-x-full");
        }, 10);
      } else {
        panel.classList.add("-translate-x-full");
        setTimeout(() => {
          overlay.classList.add("hidden");
        }, 300);
      }
    },

    close() {
      this.isOpen = false;
      const overlay = document.getElementById("mobileFilterOverlay");
      const panel = document.getElementById("mobileFilterPanel");

      panel.classList.add("-translate-x-full");
      setTimeout(() => {
        overlay.classList.add("hidden");
      }, 300);
    },
  }));

  /**
   * Dynamic Progress Bar Component - Replaces inline width styles
   */
  Alpine.data("progressBar", (percentage = 0) => ({
    percentage: percentage,

    init() {
      this.$nextTick(() => {
        this.$el.style.width = `${this.percentage}%`;
      });
    },

    setPercentage(newPercentage) {
      this.percentage = Math.max(0, Math.min(100, newPercentage));
      this.$el.style.width = `${this.percentage}%`;
    },
  }));

  /**
   * Product Component - Complete product functionality
   * Replaces inline JavaScript in box_product.inc.php
   */
  Alpine.data("product", productComponent);

  /**
   * Return Form Component - Return and exchange form functionality
   */
  Alpine.data("returnForm", returnFormComponent);

  /**
   * Order History Manager Component - Manages return form modals
   */
  Alpine.data("orderHistoryManager", orderHistoryManagerComponent);

  /**
   * Scroll To Top Component - Global scroll-to-top button
   */
  Alpine.data("scrollToTop", scrollToTopComponent);

  /**
   * Sales Urgency Components - Conversion optimization
   */
  Alpine.data("countdownTimer", countdownTimer);
  Alpine.data("stockUrgency", stockUrgency);
  Alpine.data("freeShippingProgress", freeShippingProgress);
  Alpine.data("countdown", countdown);

  /**
   * Product Click Component - Analytics tracking for product clicks
   * Replaces onclick="window.Analytics.selectItem()"
   */
  Alpine.data("productClick", (listName, index) => ({
    listName: listName,
    index: index,

    trackClick() {
      if (
        typeof window.Analytics !== "undefined" &&
        window.Analytics.selectItem
      ) {
        const productCard = this.$el.closest(".product-card");
        if (productCard) {
          window.Analytics.selectItem(productCard, {
            list_name: this.listName,
            index: this.index,
          });
        }
      }
    },
  }));

  // ========================================
  // STORES AUTO-INITIALIZE
  // ========================================
  // Note: Alpine.js automatically calls init() when stores are registered
  // via Alpine.store(). No manual init() calls needed here.
  // See: https://alpinejs.dev/globals/alpine-store#init-functions

  // ========================================
  // GLOBAL KEYBOARD SHORTCUTS
  // ========================================
  document.addEventListener("keydown", (e) => {
    // ESC key closes cart sidebar
    if (e.key === "Escape") {
      if (Alpine.store("cart").isOpen) {
        Alpine.store("cart").close();
      }
    }

    // Ctrl/Cmd + K opens search
    if ((e.ctrlKey || e.metaKey) && e.key === "k") {
      e.preventDefault();
      const searchToggle = document.getElementById("search-toggle");
      if (searchToggle) searchToggle.click();
    }
  });
});

// ========================================
// GLOBAL HELPER FUNCTIONS (Template Compatibility)
// ========================================

/**
 * Handle Maintenance Mode Notify Form Submission
 * Replaces inline onsubmit="handleNotifySubmit(event)"
 */
window.handleNotifySubmit = function (event) {
  event.preventDefault();

  const form = event.target;
  const email = form.querySelector('input[type="email"]').value;

  if (!email) {
    window.Notification?.error(window.tx('pleaseEnterEmail', 'Please enter your email.'));
    return;
  }

  // Show loading state
  window.Notification?.info(window.tx('savingInfo', 'Saving your information...'));

  // Here you would normally submit to server
  // For now, just show success message
  setTimeout(() => {
    window.Notification?.success(window.tx('notifySubscribed', 'Your notification subscription has been saved. Thank you!'));
    form.reset();
  }, 1500);
};

/**
 * Global Mobile Filter Toggle Function
 * Fallback for pages that don't use Alpine component
 */
window.toggleMobileFilter = function () {
  const overlay = document.getElementById("mobileFilterOverlay");
  const panel = document.getElementById("mobileFilterPanel");

  if (!overlay || !panel) return;

  if (overlay.classList.contains("hidden")) {
    overlay.classList.remove("hidden");
    setTimeout(() => {
      panel.classList.remove("-translate-x-full");
    }, 10);
  } else {
    panel.classList.add("-translate-x-full");
    setTimeout(() => {
      overlay.classList.add("hidden");
    }, 300);
  }
};

// ========================================
// GLOBAL HELPERS (Template Compatibility)
// ========================================

/**
 * Notification System Fallback
 * Provides window.Notification API before Alpine initializes the component
 * Queues notifications until the real component is ready
 */
window.NotificationState = {
  initialized: false,
  queue: [],
};

window.Notification = {
  success: (message, duration) => {
    if (window.NotificationState.initialized) {
      return; // Real system will handle it
    }
    window.NotificationState.queue.push({ message, type: "success", duration });
  },
  error: (message, duration) => {
    if (window.NotificationState.initialized) {
      return; // Real system will handle it
    }
    window.NotificationState.queue.push({ message, type: "error", duration });
  },
  warning: (message, duration) => {
    if (window.NotificationState.initialized) {
      return; // Real system will handle it
    }
    window.NotificationState.queue.push({ message, type: "warning", duration });
  },
  info: (message, duration) => {
    if (window.NotificationState.initialized) {
      return; // Real system will handle it
    }
    window.NotificationState.queue.push({ message, type: "info", duration });
  },
  notice: (message, duration) => {
    if (window.NotificationState.initialized) {
      return; // Real system will handle it
    }
    window.NotificationState.queue.push({ message, type: "info", duration });
  },
};

/**
 * Global cart sidebar state
 * Simple boolean for backward compatibility with templates
 */
window.cartSidebarOpen = false;

/**
 * Global cart functions for template compatibility
 * These trigger Alpine.js store methods
 */
window.openCart = () => {
  window.cartSidebarOpen = true;
  document.dispatchEvent(new CustomEvent("cart-sidebar-opened"));
};

window.closeCart = () => {
  window.cartSidebarOpen = false;
  document.dispatchEvent(new CustomEvent("cart-sidebar-closed"));
};

window.toggleCart = () => {
  window.cartSidebarOpen = !window.cartSidebarOpen;
  document.dispatchEvent(new CustomEvent("cart-sidebar-toggled"));
};


window.cart = function () {
  return {
    get isOpen() {
      return window.cartSidebarOpen;
    },

    openCart() {
      window.openCart();
    },

    closeCart() {
      window.closeCart();
    },

    toggleCart() {
      window.toggleCart();
    },

  };
};

// ========================================
// START ALPINE.JS
// ========================================
Alpine.start();